// ─── Signal Protocol-Inspired Crypto Engine ─────────────────────────────────
// Pure functions using Web Crypto API (crypto.subtle).
// Implements X3DH key exchange + Double Ratchet for E2E encryption.
// No React, no state — stateless functions that operate on typed parameters.
//
// Backend-ready: Key bundles and session structures are designed to be
// exchanged via a server (AWS Lambda / Momento Topics). In DEV_AUTH_BYPASS
// mode, both sides of the handshake run locally with mock users.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Constants ───────────────────────────────────────────────────────────────

/** Curve used for all ECDH key pairs */
const ECDH_CURVE: EcKeyGenParams = { name: "ECDH", namedCurve: "P-256" };

/** AES-GCM parameters for message encryption */
const AES_KEY_LENGTH = 256;
const AES_ALGO = "AES-GCM";

/** IV length in bytes for AES-GCM (96 bits = 12 bytes, NIST recommendation) */
const IV_LENGTH = 12;

/** HKDF info strings for domain separation */
const HKDF_INFO_ROOT = new TextEncoder().encode("SlumbrDoubleRatchetRoot");
const HKDF_INFO_CHAIN = new TextEncoder().encode("SlumbrDoubleRatchetChain");
const HKDF_INFO_MESSAGE = new TextEncoder().encode("SlumbrDoubleRatchetMessage");
const HKDF_INFO_X3DH = new TextEncoder().encode("SlumbrX3DHSharedSecret");

/** Max message keys to store per chain for out-of-order message decryption */
export const MAX_SKIP = 100;

// ─── Types ───────────────────────────────────────────────────────────────────

/** Raw ECDH key pair — the fundamental building block */
export interface ECDHKeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

/** Exportable version of a public key (for transmission/storage) */
export interface ExportedPublicKey {
  /** Raw public key bytes, base64-encoded */
  raw: string;
}

/** Identity key pair — long-term, one per user */
export interface IdentityKeyPair extends ECDHKeyPair {
  /** Fingerprint for display (hex string of SHA-256 hash of public key) */
  fingerprint: string;
}

/** Signed pre-key — medium-term, rotated periodically */
export interface SignedPreKey {
  id: number;
  keyPair: ECDHKeyPair;
  signature: ArrayBuffer; // HMAC signature of the public key using identity key
  timestamp: number;
}

/** One-time pre-key — single use, consumed during X3DH */
export interface OneTimePreKey {
  id: number;
  keyPair: ECDHKeyPair;
}

/**
 * Pre-key bundle — published to server for others to initiate sessions.
 * Contains only public keys + metadata (no private keys ever leave the device).
 */
export interface PreKeyBundle {
  userId: string;
  identityKey: ExportedPublicKey;
  identityFingerprint: string;
  signedPreKey: {
    id: number;
    publicKey: ExportedPublicKey;
    signature: string; // base64-encoded HMAC
  };
  oneTimePreKey?: {
    id: number;
    publicKey: ExportedPublicKey;
  };
}

/** Ratchet state for the Double Ratchet algorithm */
export interface RatchetState {
  /** Our current DH ratchet key pair */
  dhKeyPair: ECDHKeyPair;
  /** Their current DH ratchet public key */
  remoteDhPublicKey: CryptoKey | null;
  /** Root key — input to root chain KDF */
  rootKey: CryptoKey;
  /** Sending chain key — input to sending chain KDF */
  sendChainKey: CryptoKey | null;
  /** Receiving chain key — input to receiving chain KDF */
  recvChainKey: CryptoKey | null;
  /** Send message counter */
  sendCount: number;
  /** Receive message counter */
  recvCount: number;
  /** Previous sending chain length (for header) */
  prevSendCount: number;
  /** Skipped message keys: Map<"<ratchetPubKeyHex>:<counter>", CryptoKey> */
  skippedKeys: Map<string, CryptoKey>;
}

/**
 * Full session state between two users.
 * Stored in IndexedDB via key-store.ts.
 */
export interface EncryptionSession {
  /** Unique session ID */
  sessionId: string;
  /** Our user ID */
  localUserId: string;
  /** Remote user ID */
  remoteUserId: string;
  /** Our identity key pair (long-term) */
  localIdentityKeyPair: IdentityKeyPair;
  /** Their identity public key */
  remoteIdentityKey: CryptoKey;
  /** Their identity fingerprint */
  remoteIdentityFingerprint: string;
  /** Double Ratchet state */
  ratchetState: RatchetState;
  /** Session creation timestamp */
  createdAt: number;
  /** Last activity timestamp */
  lastActivityAt: number;
  /** Whether safety number has been verified by user */
  isVerified: boolean;
}

/** Encrypted message envelope — what gets sent over the wire */
export interface EncryptedEnvelope {
  /** Session ID this message belongs to */
  sessionId: string;
  /** Sender's current DH ratchet public key (for ratchet step) */
  senderRatchetKey: string; // base64 raw public key
  /** Message counter in current sending chain */
  counter: number;
  /** Previous chain length (for skipped key calculation) */
  prevChainLength: number;
  /** AES-GCM initialization vector */
  iv: string; // base64
  /** Encrypted message content */
  ciphertext: string; // base64
}

/**
 * Safety number — 60 digits displayed as 12 groups of 5.
 * Derived from both users' identity key fingerprints.
 */
export interface SafetyNumber {
  digits: string; // 60 numeric digits
  display: string; // "12345 67890 12345 67890 12345 67890\n12345 67890 12345 67890 12345 67890"
}

// ─── Utilities: Encoding ─────────────────────────────────────────────────────

/** Convert ArrayBuffer to base64 string */
export function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Convert base64 string to ArrayBuffer */
export function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/** Convert ArrayBuffer to hex string */
export function bufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Concatenate multiple ArrayBuffers into one */
function concatBuffers(...buffers: ArrayBuffer[]): ArrayBuffer {
  const totalLen = buffers.reduce((sum, b) => sum + b.byteLength, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const buf of buffers) {
    result.set(new Uint8Array(buf), offset);
    offset += buf.byteLength;
  }
  return result.buffer;
}

// ─── Key Generation ──────────────────────────────────────────────────────────

/** Generate an ECDH key pair (P-256) */
export async function generateECDHKeyPair(): Promise<ECDHKeyPair> {
  const keyPair = await crypto.subtle.generateKey(ECDH_CURVE, true, [
    "deriveBits",
  ]);
  return {
    publicKey: keyPair.publicKey,
    privateKey: keyPair.privateKey,
  };
}

/** Generate an identity key pair with fingerprint */
export async function generateIdentityKeyPair(): Promise<IdentityKeyPair> {
  const keyPair = await generateECDHKeyPair();
  const fingerprint = await computeFingerprint(keyPair.publicKey);
  return {
    ...keyPair,
    fingerprint,
  };
}

/** Generate a signed pre-key, signed with our identity key */
export async function generateSignedPreKey(
  identityKeyPair: IdentityKeyPair,
  id: number
): Promise<SignedPreKey> {
  const keyPair = await generateECDHKeyPair();
  const publicKeyRaw = await crypto.subtle.exportKey("raw", keyPair.publicKey);
  const signature = await hmacSign(identityKeyPair.privateKey, publicKeyRaw);
  return {
    id,
    keyPair,
    signature,
    timestamp: Date.now(),
  };
}

/** Generate a batch of one-time pre-keys */
export async function generateOneTimePreKeys(
  startId: number,
  count: number
): Promise<OneTimePreKey[]> {
  const keys: OneTimePreKey[] = [];
  for (let i = 0; i < count; i++) {
    const keyPair = await generateECDHKeyPair();
    keys.push({ id: startId + i, keyPair });
  }
  return keys;
}

// ─── Key Export / Import ─────────────────────────────────────────────────────

/** Export a CryptoKey (public) to base64 raw format */
export async function exportPublicKey(
  key: CryptoKey
): Promise<ExportedPublicKey> {
  const raw = await crypto.subtle.exportKey("raw", key);
  return { raw: bufferToBase64(raw) };
}

/** Import a public key from ExportedPublicKey format */
export async function importPublicKey(
  exported: ExportedPublicKey
): Promise<CryptoKey> {
  const raw = base64ToBuffer(exported.raw);
  return crypto.subtle.importKey("raw", raw, ECDH_CURVE, true, []);
}

// ─── Fingerprint & Safety Numbers ────────────────────────────────────────────

/** Compute a hex fingerprint of a public key (SHA-256 hash of raw bytes) */
export async function computeFingerprint(
  publicKey: CryptoKey
): Promise<string> {
  const raw = await crypto.subtle.exportKey("raw", publicKey);
  const hash = await crypto.subtle.digest("SHA-256", raw);
  return bufferToHex(hash);
}

/**
 * Generate a safety number from two identity key fingerprints.
 * Signal-style: 60 numeric digits, displayed as 12 groups of 5.
 *
 * The safety number is the same regardless of which user computes it
 * (fingerprints are sorted lexically before hashing).
 */
export async function generateSafetyNumber(
  localFingerprint: string,
  remoteFingerprint: string
): Promise<SafetyNumber> {
  // Sort fingerprints so both sides compute the same result
  const [first, second] = [localFingerprint, remoteFingerprint].sort();
  const combined = new TextEncoder().encode(first + second);

  // Hash the combined fingerprints
  const hash = await crypto.subtle.digest("SHA-256", combined);
  const bytes = new Uint8Array(hash);

  // Convert to 60 decimal digits
  // Each byte (0-255) maps to digits by taking mod 10 operations
  let digits = "";
  for (let i = 0; i < bytes.length && digits.length < 60; i++) {
    // Use each byte to generate 2 digits
    digits += (bytes[i] % 10).toString();
    digits += (Math.floor(bytes[i] / 10) % 10).toString();
  }
  digits = digits.slice(0, 60);

  // Format as 12 groups of 5 digits, 6 groups per line
  const groups: string[] = [];
  for (let i = 0; i < 60; i += 5) {
    groups.push(digits.slice(i, i + 5));
  }
  const line1 = groups.slice(0, 6).join(" ");
  const line2 = groups.slice(6, 12).join(" ");
  const display = `${line1}\n${line2}`;

  return { digits, display };
}

// ─── ECDH Shared Secret ──────────────────────────────────────────────────────

/** Perform ECDH to derive shared bits from a private key and a remote public key */
async function ecdh(
  privateKey: CryptoKey,
  publicKey: CryptoKey
): Promise<ArrayBuffer> {
  return crypto.subtle.deriveBits(
    { name: "ECDH", public: publicKey },
    privateKey,
    256 // P-256 produces 256 bits
  );
}

// ─── HKDF Key Derivation ────────────────────────────────────────────────────

/**
 * Derive a CryptoKey from input key material using HKDF.
 * Returns an AES-GCM key usable for encryption or further derivation.
 */
async function hkdfDerive(
  inputKeyMaterial: ArrayBuffer,
  salt: ArrayBuffer,
  info: BufferSource,
  usage: KeyUsage[] = ["encrypt", "decrypt"]
): Promise<CryptoKey> {
  // Import the raw bytes as HKDF key material
  const hkdfKey = await crypto.subtle.importKey(
    "raw",
    inputKeyMaterial,
    "HKDF",
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(salt) as BufferSource,
      info: info,
    },
    hkdfKey,
    { name: AES_ALGO, length: AES_KEY_LENGTH },
    true, // extractable so we can feed it back into HKDF
    usage
  );
}

/**
 * Derive a CryptoKey as HKDF output, using another CryptoKey as salt.
 * Used in the ratchet chain where both inputs are CryptoKeys.
 */
async function hkdfDeriveFromKeys(
  inputKey: CryptoKey,
  saltKey: CryptoKey,
  info: BufferSource,
  usage: KeyUsage[] = ["encrypt", "decrypt"]
): Promise<CryptoKey> {
  const inputRaw = await crypto.subtle.exportKey("raw", inputKey);
  const saltRaw = await crypto.subtle.exportKey("raw", saltKey);
  return hkdfDerive(inputRaw, saltRaw, info, usage);
}

// ─── HMAC (for signed pre-keys) ─────────────────────────────────────────────

/**
 * Sign data using ECDH private key via HMAC.
 * (In a real implementation this would use EdDSA signatures, but Web Crypto
 * doesn't support Ed25519 widely yet. HMAC with exported key material
 * provides integrity verification for our DEV_AUTH_BYPASS scenario.)
 */
async function hmacSign(
  privateKey: CryptoKey,
  data: ArrayBuffer
): Promise<ArrayBuffer> {
  const keyRaw = await crypto.subtle.exportKey("pkcs8", privateKey);
  const hmacKey = await crypto.subtle.importKey(
    "raw",
    keyRaw.slice(0, 32), // Use first 32 bytes as HMAC key
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return crypto.subtle.sign("HMAC", hmacKey, data);
}

// ─── X3DH Key Exchange ──────────────────────────────────────────────────────

/**
 * Build a pre-key bundle from our keys (to publish to server).
 * Other users fetch this to initiate a session with us.
 */
export async function buildPreKeyBundle(
  userId: string,
  identityKeyPair: IdentityKeyPair,
  signedPreKey: SignedPreKey,
  oneTimePreKey?: OneTimePreKey
): Promise<PreKeyBundle> {
  const identityKey = await exportPublicKey(identityKeyPair.publicKey);
  const spkPublicKey = await exportPublicKey(signedPreKey.keyPair.publicKey);

  const bundle: PreKeyBundle = {
    userId,
    identityKey,
    identityFingerprint: identityKeyPair.fingerprint,
    signedPreKey: {
      id: signedPreKey.id,
      publicKey: spkPublicKey,
      signature: bufferToBase64(signedPreKey.signature),
    },
  };

  if (oneTimePreKey) {
    const otkPublicKey = await exportPublicKey(oneTimePreKey.keyPair.publicKey);
    bundle.oneTimePreKey = {
      id: oneTimePreKey.id,
      publicKey: otkPublicKey,
    };
  }

  return bundle;
}

/**
 * X3DH: Initiator side — compute shared secret from recipient's pre-key bundle.
 *
 * Performs 3 (or 4) ECDH operations:
 *   DH1 = ECDH(ourIdentity, theirSignedPreKey)
 *   DH2 = ECDH(ourEphemeral, theirIdentity)
 *   DH3 = ECDH(ourEphemeral, theirSignedPreKey)
 *   DH4 = ECDH(ourEphemeral, theirOneTimePreKey) — if available
 *
 * Returns the shared secret and the ephemeral key used.
 */
export async function x3dhInitiate(
  ourIdentity: IdentityKeyPair,
  recipientBundle: PreKeyBundle
): Promise<{
  sharedSecret: CryptoKey;
  ephemeralKeyPair: ECDHKeyPair;
  usedOneTimePreKeyId?: number;
}> {
  // Generate ephemeral key pair
  const ephemeralKeyPair = await generateECDHKeyPair();

  // Import recipient's public keys
  const theirIdentity = await importPublicKey(recipientBundle.identityKey);
  const theirSignedPreKey = await importPublicKey(
    recipientBundle.signedPreKey.publicKey
  );

  // DH1: ourIdentity <-> theirSignedPreKey
  const dh1 = await ecdh(ourIdentity.privateKey, theirSignedPreKey);
  // DH2: ourEphemeral <-> theirIdentity
  const dh2 = await ecdh(ephemeralKeyPair.privateKey, theirIdentity);
  // DH3: ourEphemeral <-> theirSignedPreKey
  const dh3 = await ecdh(ephemeralKeyPair.privateKey, theirSignedPreKey);

  let dhConcat: ArrayBuffer;
  let usedOneTimePreKeyId: number | undefined;

  if (recipientBundle.oneTimePreKey) {
    // DH4: ourEphemeral <-> theirOneTimePreKey
    const theirOneTimePreKey = await importPublicKey(
      recipientBundle.oneTimePreKey.publicKey
    );
    const dh4 = await ecdh(ephemeralKeyPair.privateKey, theirOneTimePreKey);
    dhConcat = concatBuffers(dh1, dh2, dh3, dh4);
    usedOneTimePreKeyId = recipientBundle.oneTimePreKey.id;
  } else {
    dhConcat = concatBuffers(dh1, dh2, dh3);
  }

  // Derive the shared secret using HKDF
  const salt = new ArrayBuffer(32); // Zero salt for X3DH (per Signal spec)
  const sharedSecret = await hkdfDerive(dhConcat, salt, HKDF_INFO_X3DH);

  return { sharedSecret, ephemeralKeyPair, usedOneTimePreKeyId };
}

/**
 * X3DH: Responder side — compute shared secret from initiator's message.
 *
 * Mirrors the initiator's DH operations using our private keys.
 */
export async function x3dhRespond(
  ourIdentity: IdentityKeyPair,
  ourSignedPreKey: SignedPreKey,
  ourOneTimePreKey: OneTimePreKey | undefined,
  theirIdentityKey: CryptoKey,
  theirEphemeralKey: CryptoKey
): Promise<CryptoKey> {
  // DH1: theirIdentity <-> ourSignedPreKey
  const dh1 = await ecdh(ourSignedPreKey.keyPair.privateKey, theirIdentityKey);
  // DH2: theirEphemeral <-> ourIdentity
  const dh2 = await ecdh(ourIdentity.privateKey, theirEphemeralKey);
  // DH3: theirEphemeral <-> ourSignedPreKey
  const dh3 = await ecdh(
    ourSignedPreKey.keyPair.privateKey,
    theirEphemeralKey
  );

  let dhConcat: ArrayBuffer;

  if (ourOneTimePreKey) {
    // DH4: theirEphemeral <-> ourOneTimePreKey
    const dh4 = await ecdh(
      ourOneTimePreKey.keyPair.privateKey,
      theirEphemeralKey
    );
    dhConcat = concatBuffers(dh1, dh2, dh3, dh4);
  } else {
    dhConcat = concatBuffers(dh1, dh2, dh3);
  }

  // Derive the shared secret using HKDF (same as initiator)
  const salt = new ArrayBuffer(32);
  return hkdfDerive(dhConcat, salt, HKDF_INFO_X3DH);
}

// ─── Double Ratchet ──────────────────────────────────────────────────────────

/**
 * Initialize a ratchet state after X3DH completes (initiator side).
 * The initiator sends first, so they set up the sending chain.
 */
export async function initRatchetAsInitiator(
  sharedSecret: CryptoKey,
  recipientSignedPreKeyPublic: CryptoKey
): Promise<RatchetState> {
  // Generate our first ratchet key pair
  const dhKeyPair = await generateECDHKeyPair();

  // Perform DH ratchet step: DH(ourRatchet, theirSignedPreKey)
  const dhOutput = await ecdh(
    dhKeyPair.privateKey,
    recipientSignedPreKeyPublic
  );

  // KDF to get new root key and sending chain key
  const sharedSecretRaw = await crypto.subtle.exportKey("raw", sharedSecret);
  const rootKey = await hkdfDerive(dhOutput, sharedSecretRaw, HKDF_INFO_ROOT);
  const sendChainKey = await hkdfDerive(
    dhOutput,
    sharedSecretRaw,
    HKDF_INFO_CHAIN
  );

  return {
    dhKeyPair,
    remoteDhPublicKey: recipientSignedPreKeyPublic,
    rootKey,
    sendChainKey,
    recvChainKey: null,
    sendCount: 0,
    recvCount: 0,
    prevSendCount: 0,
    skippedKeys: new Map(),
  };
}

/**
 * Initialize a ratchet state after X3DH completes (responder side).
 * The responder receives first, so they set up with the signed pre-key
 * as their current DH key pair.
 */
export async function initRatchetAsResponder(
  sharedSecret: CryptoKey,
  ourSignedPreKey: SignedPreKey
): Promise<RatchetState> {
  return {
    dhKeyPair: ourSignedPreKey.keyPair,
    remoteDhPublicKey: null,
    rootKey: sharedSecret,
    sendChainKey: null,
    recvChainKey: null,
    sendCount: 0,
    recvCount: 0,
    prevSendCount: 0,
    skippedKeys: new Map(),
  };
}

/**
 * Perform a DH ratchet step (when receiving a new ratchet public key).
 * Updates the root key and derives new receiving/sending chain keys.
 */
async function dhRatchetStep(
  state: RatchetState,
  newRemotePublicKey: CryptoKey
): Promise<RatchetState> {
  // Store previous send count
  const prevSendCount = state.sendCount;

  // DH with new remote key and our current key pair → new receiving chain
  const dhRecv = await ecdh(state.dhKeyPair.privateKey, newRemotePublicKey);
  const rootKeyRaw = await crypto.subtle.exportKey("raw", state.rootKey);
  const newRootKey1 = await hkdfDerive(dhRecv, rootKeyRaw, HKDF_INFO_ROOT);
  const recvChainKey = await hkdfDerive(dhRecv, rootKeyRaw, HKDF_INFO_CHAIN);

  // Generate new DH key pair for our side
  const newDhKeyPair = await generateECDHKeyPair();

  // DH with new remote key and our NEW key pair → new sending chain
  const dhSend = await ecdh(newDhKeyPair.privateKey, newRemotePublicKey);
  const newRootKeyRaw = await crypto.subtle.exportKey("raw", newRootKey1);
  const newRootKey2 = await hkdfDerive(dhSend, newRootKeyRaw, HKDF_INFO_ROOT);
  const sendChainKey = await hkdfDerive(
    dhSend,
    newRootKeyRaw,
    HKDF_INFO_CHAIN
  );

  return {
    ...state,
    dhKeyPair: newDhKeyPair,
    remoteDhPublicKey: newRemotePublicKey,
    rootKey: newRootKey2,
    sendChainKey,
    recvChainKey,
    sendCount: 0,
    recvCount: 0,
    prevSendCount,
  };
}

/**
 * Advance a chain key by one step, producing a message key and the next chain key.
 * This is the symmetric ratchet step.
 */
async function advanceChainKey(
  chainKey: CryptoKey
): Promise<{ messageKey: CryptoKey; nextChainKey: CryptoKey }> {
  const chainKeyRaw = await crypto.subtle.exportKey("raw", chainKey);
  const salt = new ArrayBuffer(32); // Zero salt

  const messageKey = await hkdfDerive(
    chainKeyRaw,
    salt,
    HKDF_INFO_MESSAGE,
    ["encrypt", "decrypt"]
  );

  // Derive next chain key using chain info
  const nextChainKey = await hkdfDerive(chainKeyRaw, salt, HKDF_INFO_CHAIN);

  return { messageKey, nextChainKey };
}

/**
 * Skip ahead in the receiving chain to handle out-of-order messages.
 * Stores skipped message keys in the state for later decryption.
 */
async function skipMessageKeys(
  state: RatchetState,
  remotePubKeyHex: string,
  until: number
): Promise<RatchetState> {
  if (!state.recvChainKey) return state;
  if (until - state.recvCount > MAX_SKIP) {
    throw new Error(
      `Too many skipped messages (${until - state.recvCount} > ${MAX_SKIP})`
    );
  }

  let { recvChainKey, recvCount, skippedKeys } = state;
  skippedKeys = new Map(skippedKeys); // Clone to avoid mutation

  while (recvCount < until) {
    const { messageKey, nextChainKey } = await advanceChainKey(recvChainKey!);
    skippedKeys.set(`${remotePubKeyHex}:${recvCount}`, messageKey);
    recvChainKey = nextChainKey;
    recvCount++;
  }

  return { ...state, recvChainKey, recvCount, skippedKeys };
}

// ─── Message Encryption / Decryption ─────────────────────────────────────────

/**
 * Encrypt a plaintext message using the Double Ratchet.
 * Returns the updated ratchet state and the encrypted envelope.
 */
export async function ratchetEncrypt(
  state: RatchetState,
  plaintext: string
): Promise<{ state: RatchetState; envelope: EncryptedEnvelope }> {
  if (!state.sendChainKey) {
    throw new Error("No sending chain key — session not fully initialized");
  }

  // Advance the sending chain
  const { messageKey, nextChainKey } = await advanceChainKey(
    state.sendChainKey
  );

  // Export our ratchet public key for the header
  const senderRatchetKeyRaw = await crypto.subtle.exportKey(
    "raw",
    state.dhKeyPair.publicKey
  );

  // Encrypt the plaintext
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const plaintextBytes = new TextEncoder().encode(plaintext);

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: AES_ALGO, iv: iv as BufferSource },
    messageKey,
    plaintextBytes
  );

  const envelope: EncryptedEnvelope = {
    sessionId: "", // Filled in by the caller (session layer)
    senderRatchetKey: bufferToBase64(senderRatchetKeyRaw),
    counter: state.sendCount,
    prevChainLength: state.prevSendCount,
    iv: bufferToBase64(iv.buffer),
    ciphertext: bufferToBase64(ciphertextBuffer),
  };

  const newState: RatchetState = {
    ...state,
    sendChainKey: nextChainKey,
    sendCount: state.sendCount + 1,
  };

  return { state: newState, envelope };
}

/**
 * Decrypt an encrypted envelope using the Double Ratchet.
 * Handles DH ratchet steps and out-of-order messages.
 * Returns the updated ratchet state and the decrypted plaintext.
 */
export async function ratchetDecrypt(
  state: RatchetState,
  envelope: EncryptedEnvelope
): Promise<{ state: RatchetState; plaintext: string }> {
  // Import the sender's ratchet key from the envelope
  const senderRatchetKeyRaw = base64ToBuffer(envelope.senderRatchetKey);
  const senderRatchetKey = await crypto.subtle.importKey(
    "raw",
    senderRatchetKeyRaw,
    ECDH_CURVE,
    true,
    []
  );
  const senderKeyHex = bufferToHex(senderRatchetKeyRaw);

  // Check if this is a skipped message
  const skippedKey = state.skippedKeys.get(
    `${senderKeyHex}:${envelope.counter}`
  );
  if (skippedKey) {
    const plaintext = await decryptWithKey(skippedKey, envelope);
    const skippedKeys = new Map(state.skippedKeys);
    skippedKeys.delete(`${senderKeyHex}:${envelope.counter}`);
    return { state: { ...state, skippedKeys }, plaintext };
  }

  let currentState = state;

  // Check if we need a DH ratchet step (new ratchet key from sender)
  const isNewRatchetKey = await isNewRemoteKey(
    state.remoteDhPublicKey,
    senderRatchetKey
  );

  if (isNewRatchetKey) {
    // Skip any missed messages in the current receiving chain
    if (state.remoteDhPublicKey && state.recvChainKey) {
      const currentRemoteHex = bufferToHex(
        await crypto.subtle.exportKey("raw", state.remoteDhPublicKey)
      );
      currentState = await skipMessageKeys(
        currentState,
        currentRemoteHex,
        envelope.prevChainLength
      );
    }

    // Perform DH ratchet step
    currentState = await dhRatchetStep(currentState, senderRatchetKey);
  }

  // Skip ahead if there are gaps in the counter
  currentState = await skipMessageKeys(
    currentState,
    senderKeyHex,
    envelope.counter
  );

  // Advance the receiving chain one more step for this message
  if (!currentState.recvChainKey) {
    throw new Error("No receiving chain key after ratchet step");
  }
  const { messageKey, nextChainKey } = await advanceChainKey(
    currentState.recvChainKey
  );

  // Decrypt
  const plaintext = await decryptWithKey(messageKey, envelope);

  const newState: RatchetState = {
    ...currentState,
    recvChainKey: nextChainKey,
    recvCount: currentState.recvCount + 1,
  };

  return { state: newState, plaintext };
}

/** Decrypt an envelope with a specific AES-GCM message key */
async function decryptWithKey(
  messageKey: CryptoKey,
  envelope: EncryptedEnvelope
): Promise<string> {
  const iv = new Uint8Array(base64ToBuffer(envelope.iv));
  const ciphertext = base64ToBuffer(envelope.ciphertext);

  const plaintextBuffer = await crypto.subtle.decrypt(
    { name: AES_ALGO, iv: iv as BufferSource },
    messageKey,
    ciphertext
  );

  return new TextDecoder().decode(plaintextBuffer);
}

/** Check if a remote public key is different from the current one */
async function isNewRemoteKey(
  currentKey: CryptoKey | null,
  newKey: CryptoKey
): Promise<boolean> {
  if (!currentKey) return true;
  const currentRaw = await crypto.subtle.exportKey("raw", currentKey);
  const newRaw = await crypto.subtle.exportKey("raw", newKey);
  const currentBytes = new Uint8Array(currentRaw);
  const newBytes = new Uint8Array(newRaw);
  if (currentBytes.length !== newBytes.length) return true;
  for (let i = 0; i < currentBytes.length; i++) {
    if (currentBytes[i] !== newBytes[i]) return true;
  }
  return false;
}

// ─── Full Session Management ─────────────────────────────────────────────────

/**
 * Create a new encrypted session as the initiator.
 * Performs X3DH and initializes the Double Ratchet.
 *
 * In DEV_AUTH_BYPASS mode, this is called when a user enables encryption
 * for a conversation. We simulate the recipient's side locally.
 */
export async function createSession(
  sessionId: string,
  localUserId: string,
  localIdentityKeyPair: IdentityKeyPair,
  recipientBundle: PreKeyBundle
): Promise<EncryptionSession> {
  // Perform X3DH
  const { sharedSecret } = await x3dhInitiate(
    localIdentityKeyPair,
    recipientBundle
  );

  // Import recipient's signed pre-key for the initial ratchet step
  const recipientSignedPreKeyPublic = await importPublicKey(
    recipientBundle.signedPreKey.publicKey
  );

  // Initialize the Double Ratchet
  const ratchetState = await initRatchetAsInitiator(
    sharedSecret,
    recipientSignedPreKeyPublic
  );

  // Import recipient's identity key for storage
  const remoteIdentityKey = await importPublicKey(recipientBundle.identityKey);

  return {
    sessionId,
    localUserId,
    remoteUserId: recipientBundle.userId,
    localIdentityKeyPair,
    remoteIdentityKey,
    remoteIdentityFingerprint: recipientBundle.identityFingerprint,
    ratchetState,
    createdAt: Date.now(),
    lastActivityAt: Date.now(),
    isVerified: false,
  };
}

/**
 * Encrypt a message within a session.
 * Returns the updated session and the encrypted envelope.
 */
export async function encryptMessage(
  session: EncryptionSession,
  plaintext: string
): Promise<{ session: EncryptionSession; envelope: EncryptedEnvelope }> {
  const { state, envelope } = await ratchetEncrypt(
    session.ratchetState,
    plaintext
  );

  // Stamp the session ID onto the envelope
  envelope.sessionId = session.sessionId;

  return {
    session: {
      ...session,
      ratchetState: state,
      lastActivityAt: Date.now(),
    },
    envelope,
  };
}

/**
 * Decrypt a message within a session.
 * Returns the updated session and the decrypted plaintext.
 */
export async function decryptMessage(
  session: EncryptionSession,
  envelope: EncryptedEnvelope
): Promise<{ session: EncryptionSession; plaintext: string }> {
  const { state, plaintext } = await ratchetDecrypt(
    session.ratchetState,
    envelope
  );

  return {
    session: {
      ...session,
      ratchetState: state,
      lastActivityAt: Date.now(),
    },
    plaintext,
  };
}

// ─── DEV_AUTH_BYPASS: Local Mock Key Exchange ────────────────────────────────
// In dev mode, we simulate both sides of the X3DH handshake locally
// since there's no server to relay key bundles.

/**
 * Generate a complete set of keys for a mock user.
 * Returns everything needed to build a pre-key bundle and respond to X3DH.
 */
export async function generateMockUserKeys(userId: string): Promise<{
  identityKeyPair: IdentityKeyPair;
  signedPreKey: SignedPreKey;
  oneTimePreKeys: OneTimePreKey[];
  preKeyBundle: PreKeyBundle;
}> {
  const identityKeyPair = await generateIdentityKeyPair();
  const signedPreKey = await generateSignedPreKey(identityKeyPair, 1);
  const oneTimePreKeys = await generateOneTimePreKeys(1, 5);

  const preKeyBundle = await buildPreKeyBundle(
    userId,
    identityKeyPair,
    signedPreKey,
    oneTimePreKeys[0]
  );

  return { identityKeyPair, signedPreKey, oneTimePreKeys, preKeyBundle };
}

/**
 * Create a pair of sessions for local dev mode (both sides of the handshake).
 * Returns the initiator's session ready for use.
 *
 * In production, this would be split across two devices/users
 * communicating via the server.
 */
export async function createDevSession(
  sessionId: string,
  localUserId: string,
  remoteUserId: string,
  localIdentityKeyPair: IdentityKeyPair,
  localSignedPreKey: SignedPreKey
): Promise<{
  initiatorSession: EncryptionSession;
  responderSession: EncryptionSession;
}> {
  // Generate remote user's keys
  const remoteKeys = await generateMockUserKeys(remoteUserId);

  // Initiator (local user) creates session using remote's bundle
  const initiatorSession = await createSession(
    sessionId,
    localUserId,
    localIdentityKeyPair,
    remoteKeys.preKeyBundle
  );

  // Responder (remote user) creates their side using our public info
  const sharedSecret = await x3dhRespond(
    remoteKeys.identityKeyPair,
    remoteKeys.signedPreKey,
    remoteKeys.oneTimePreKeys[0],
    localIdentityKeyPair.publicKey,
    initiatorSession.ratchetState.dhKeyPair.publicKey
  );

  const responderRatchet = await initRatchetAsResponder(
    sharedSecret,
    remoteKeys.signedPreKey
  );

  const responderSession: EncryptionSession = {
    sessionId: `${sessionId}-responder`,
    localUserId: remoteUserId,
    remoteUserId: localUserId,
    localIdentityKeyPair: remoteKeys.identityKeyPair,
    remoteIdentityKey: localIdentityKeyPair.publicKey,
    remoteIdentityFingerprint: localIdentityKeyPair.fingerprint,
    ratchetState: responderRatchet,
    createdAt: Date.now(),
    lastActivityAt: Date.now(),
    isVerified: false,
  };

  return { initiatorSession, responderSession };
}
